import * as _ from 'lodash';
import moment from 'moment';
import qs from 'qs';
import { createSelector } from 'reselect';

import { thirdPartyFetchersMap } from '../../../shared/fetchers';
import Locator from '../../../shared/Locator';
import {
  CVSSv2BaseMetricNames,
  CVSSv3BaseMetricNames,
  CVSSv4BaseMetricNames,
  getCVSSV2HumanReadableMetrics,
  getCVSSV3HumanReadableMetrics,
  getCVSSV4HumanReadableMetrics,
} from '../../../shared/vulns/cvssUtils';
import { UNABLE_TO_CALCULATE } from '../../../shared/vulns/remediation';
import { flattenFilters } from '../../helpers/packageInventory';
import { formatNumberWithCommas, getHumanReadableEPSS, selectOptionFactory } from '../../utils';
import { getTeamSelection } from '../teams/selectors';
import type { Package } from '../../../shared/organizationPackages';
import type { CVSSMetric } from '../../../shared/vulns/types';
import type { PackageInventoryQueryParams } from '../../helpers/packageInventory';
import type { ReduxStore } from '../store';

import type { PackageInventoryStore } from './types';

export const getPackageInventoryStore = (state: ReduxStore): PackageInventoryStore => state.packageInventory;
const createPackageInventorySelector = <R>(selectorFn: (state: PackageInventoryStore) => R) =>
  createSelector(getPackageInventoryStore, selectorFn);

//  Async status selectors
const getAsyncStatuses = createPackageInventorySelector((state: PackageInventoryStore) => state.asyncStatus);
export const getAsyncStatusGetPackages = createSelector(getAsyncStatuses, (asyncStatus) => asyncStatus.getPackages);
export const getAsyncStatusGetPackageDetails = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.getPackageDetails);
export const getAsyncStatusGetPackageProjects = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.getPackageProjects);
export const getAsyncStatusGetPackageMetadata = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.getPackageMetadata);
export const getAsyncStatusGetPackageVersionMetadata = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.getPackageVersionMetadata);
export const getAsyncStatusGetPackageManagers = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.getPackageManagers);
export const getAsyncStatusGetPackageLocators = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.getPackageLocators);
export const getAsyncStatusGetCwes = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.getCwes);
export const getAsyncStatusGetSelectedVuln = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.getSelectedVuln);
export const getAsyncStatusBlockPackageVersions = createSelector(getAsyncStatuses,
  (asyncStatus) => asyncStatus.blockPackageVersions);

//  Packages
export const getPackages = createPackageInventorySelector((state: PackageInventoryStore) => state.packages);
export const getTotalPackagesCount = createPackageInventorySelector(
  (state: PackageInventoryStore): number => state.totalPackagesCount,
);
export const getFirstPackage = createSelector([getPackages], (packages): Package | undefined => _.head(packages));

// Package Details
export const getPackageDetails = createPackageInventorySelector((state: PackageInventoryStore) => state.details);
export const getPackageMetaDataLoading = createSelector(
  [getAsyncStatusGetPackages, getAsyncStatusGetPackageMetadata],
  (packagesAsyncStatus, packageMetadataAsyncStatus) => {
    return packagesAsyncStatus.isLoading || packageMetadataAsyncStatus.isLoading;
  },
);
export const getPackageDetailsLoading = createSelector(
  [getAsyncStatusGetPackages, getAsyncStatusGetPackageDetails],
  (packagesAsyncStatus, packageDetailsAsyncStatus) => {
    return packagesAsyncStatus.isLoading || packageDetailsAsyncStatus.isLoading;
  },
);
export const getPackageDetailsPageLoading = createSelector([
  getPackageMetaDataLoading,
  getPackageDetailsLoading,
], (packageMetaDataLoading, packageDetailsLoading) => packageMetaDataLoading || packageDetailsLoading);

//  Package Projects
export const getPackageProjects = createPackageInventorySelector((state: PackageInventoryStore) => state.projects);
export const getTotalPackageProjectsCount = createPackageInventorySelector(
  (state: PackageInventoryStore): number => state.totalProjectsCount,
);

// Package Metadata
export const getPackageMetadata = createPackageInventorySelector(
  (state: PackageInventoryStore) => state.packageMetadata,
);

//  Package version metadata
export const getPackageVersionMetadata = createPackageInventorySelector(
  (state: PackageInventoryStore) => state.packageVersionMetadata,
);
export const getPackageVersionMetadataLocatorParts = createSelector([
  getPackageVersionMetadata,
], (packageVersionMetadata) => {
  if (!packageVersionMetadata) {
    return null;
  }
  return Locator.parse(packageVersionMetadata.locator);
});

// Package summary
export const getAsyncStatusGetPackageSummary =
  createPackageInventorySelector((state: PackageInventoryStore) => state.asyncStatus.getPackageSummary);
const getTotalPackageCount = createPackageInventorySelector(state => {
  const { count } = state.packageSummary || {};
  if (!_.isNumber(count)) {
    return 'N/A';
  }

  return formatNumberWithCommas(count);
});
const getLastCacheDate = createPackageInventorySelector(state => {
  const { lastCacheDate } = state.packageSummary || {};
  if (!lastCacheDate) {
    return null;
  }
  const date = moment(lastCacheDate);

  return date.format('MMMM Do YYYY, h:mm:ss a');
});
export const getPackageSummary = createSelector([getTotalPackageCount, getLastCacheDate], (count, lastCacheDate) => ({
  count,
  lastCacheDate,
}));

//  Filters
export const getFilters = createPackageInventorySelector((state: PackageInventoryStore) => state.filter);
export const getFiltersFetchers = createSelector([getFilters], filter => filter?.fetchers || []);
//  Returns MultiSelectFilterOptions for each of the given fetchers we have
export const getFiltersFetchersOptions = createSelector([
  getFiltersFetchers,
], (fetchers) => fetchers.map(fetcher => selectOptionFactory(fetcher, thirdPartyFetchersMap[fetcher])));

export const getFiltersLocators = createSelector([getFilters], filter => filter?.locators || []);
export const getFiltersLocatorsOptions = createSelector([getFiltersLocators], (locators) =>
  locators.map(locator => selectOptionFactory(locator)));

// Package Labels filter
export const getFiltersPackageLabels = createSelector([getFilters], (filter) => filter.packageLabels ?? []);

export const getDepthFilters = createSelector([getFilters], filter => filter?.depth || []);
export const getBlockTypeFilters = createSelector([getFilters], filter => filter.blockTypes || []);
// Project Name filter
export const getFiltersProjectName = createSelector([getFilters], filter => filter.projectName ?? '');
export const getFiltersPackageName = createSelector([getFilters], filter => filter.packageName ?? '');
// Project Visibility
export const getFiltersProjectVisibility = createSelector([getFilters], filter => filter?.visibility ?? []);
// Project Source
export const getFiltersProjectSources = createSelector([getFilters], filter => filter?.sources ?? []);
//  Project labels
export const getFiltersProjectLabels = createSelector([getFilters], filter => filter?.labels ?? []);

// Vuln CVE
export const getFiltersVulnCVE = createSelector([getFilters], filter => filter?.cve ?? '');
// Vuln CWEs
export const getFiltersVulnCWEs = createSelector([getFilters], filter => filter?.cwes ?? []);
// Vuln fixTypes
export const getFiltersVulnFixTypes = createSelector([getFilters], filter => filter?.fixTypes ?? []);
// Vuln Severities
export const getFiltersVulnSeverities = createSelector([getFilters], filter => filter?.severities ?? []);

//  Pagination
export const getPackagesPage = createPackageInventorySelector(
  (state: PackageInventoryStore) => state.pagination.packages.page,
);
export const getPackagesCount = createPackageInventorySelector(
  (state: PackageInventoryStore) => state.pagination.packages.count,
);
export const getVersionDetailsPage = createPackageInventorySelector(
  (state: PackageInventoryStore) => state.pagination.versionDetails.page,
);
export const getVersionDetailsCount = createPackageInventorySelector(
  (state: PackageInventoryStore) => state.pagination.versionDetails.count,
);

// Sort
const getSort = createPackageInventorySelector((state: PackageInventoryStore) => state.sort);
export const getSortOrDefault = createSelector([getSort, getFilters], (sort, filters) => {
  if (sort) {
    // reset match sort to alphabetical sort when removing package name filter
    if (sort === 'match' && !filters.packageName) {
      return 'alphabetical';
    }
    // reset alphabetical sort to match sort when entering package name filter
    if (sort === 'alphabetical' && filters.packageName) {
      return 'match';
    }

    return sort;
  }

  // No sort in redux, provide the default base on the package name filter.
  if (filters.packageName) {
    return 'match';
  }
  return 'alphabetical';
});

//  This grabs the current packages state so that it can be merged with the query params
export const getHydratePackagesStateQuery = createSelector([
  getPackagesPage,
  getPackagesCount,
  getFilters,
  getSortOrDefault,
  getTeamSelection,
], (page, count, filter, sort, teamIds) => flattenFilters({
  page,
  count,
  sort,
  filter: {
    ...filter,
    teamIds,
  },
}));

//  This grabs the current version details state so that it can be merged with the query params
export const getHydrateVersionDetailsStateQuery = createSelector([
  getVersionDetailsPage,
  getVersionDetailsCount,
  getFilters,
  getSortOrDefault,
  getTeamSelection,
], (page, count, filter, sort, teamIds) => flattenFilters({
  page,
  count,
  sort,
  filter: {
    ...filter,
    teamIds,
  },
}));

const omitPagination = (query: Partial<PackageInventoryQueryParams>) => (_.omit(query, ['page', 'count']));

// This grabs the query string version of the current packages state.
export const getPackagesStateQueryString = createSelector([getHydratePackagesStateQuery], (stateQuery) => {
  //  TeamIDS should not appear in the URL, since they have no effect on the TeamSelector. Later we can fix this, but this is it works in the projects dashboard currently.
  return qs.stringify(_.omit(stateQuery, ['teamIds']));
});

// This grabs the query string version of the current packages state (with pagination omitted).
export const getUnpaginatedPackagesStateQueryString = createSelector([getHydratePackagesStateQuery], (stateQuery) => {
  return qs.stringify(omitPagination(stateQuery));
});

// This grabs the query string version of the current version details state.
export const getVersionDetailsStateQueryString = createSelector([getHydrateVersionDetailsStateQuery], (stateQuery) => {
  return qs.stringify(stateQuery);
});

// This grabs the query string version of the current version details state with the page param set to 1.
export const getVersionDetailsStateQueryStringWithPaginationReset =
  createSelector([getHydrateVersionDetailsStateQuery], (stateQuery) => {
    return qs.stringify({ ...stateQuery, page: 1 });
  });

export const getHasNoPackages = createSelector([getPackages], packages => packages.length === 0);

//  Check whether any filters or a search are currently applied.
export const getHasFilterApplied = createSelector([getFilters], filters =>
  Object.values(filters || {}).some(filter => (filter || []).length > 0));

export const getHasFilterAndNoPackages = createSelector([
  getHasNoPackages,
  getHasFilterApplied,
], (hasNoPackages, hasSearchOrFilterApplied) => hasNoPackages && hasSearchOrFilterApplied);

export const getHasFilterAndNoPackageProjects = createSelector([
  getPackageProjects,
  getHasFilterApplied,
], (packageProjects, hasSearchOrFilterApplied) =>
  packageProjects && !packageProjects.length && hasSearchOrFilterApplied);

//  Check whether any API errors have occurred.
export const getHasPackageErrors = createSelector([
  getAsyncStatusGetPackages,
  getAsyncStatusGetPackageDetails,
], (getPackagesAsyncStatus, getPackageDetailsAsyncStatus) => {
  return !_.isEmpty(getPackagesAsyncStatus.errors) || !_.isEmpty(getPackageDetailsAsyncStatus.errors);
});

export const getPackageManagers = createPackageInventorySelector(state => state.packageManagers);
export const getPackageManagersSorted = createSelector([
  getPackageManagers,
], packageManagers => [...packageManagers].sort());

export const getPackageLocators = createPackageInventorySelector(state => state.locators);
export const getPackageLocatorsOptions = createSelector([getPackageLocators], (locatorResult) =>
  locatorResult.map((locator) => {
    return selectOptionFactory(locator);
  }),
);

const getCwes = createPackageInventorySelector(state => state.cwes);
export const getCwesOptions = createSelector([getCwes], cwes => cwes.map(cwe => selectOptionFactory(`CWE-${cwe.id}`)));

export const getVulnerabilities = createPackageInventorySelector(state => state.vulnerabilities);
export const getVulnerabilitiesCount = createPackageInventorySelector(state => state.vulnerabilities?.length ?? 0);
export const getAsyncStatusGetPackageVulnerabilities =
  createPackageInventorySelector(state => state.asyncStatus.getPackageVulnerabilities);
export const getIsVulnerabilitiesEmpty =
  createSelector([getVulnerabilities, getAsyncStatusGetPackageVulnerabilities], (vulns, asyncStatus) => {
    return Array.isArray(vulns) && _.isEmpty(vulns) && !asyncStatus.isLoading;
  });

export const getSelectedVuln = createPackageInventorySelector(state => state.selectedVuln);
//  We use the first `weaknesses` entry to determine the title & description.
export const getSelectedVulnFirstWeakness = createSelector([getSelectedVuln], (selectedVuln) => {
  return selectedVuln ? _.head(selectedVuln.weaknesses) : null;
});
export const getSelectedVulnTitle = createSelector([getSelectedVulnFirstWeakness], (weakness) => {
  return weakness?.title ?? 'General Vulnerability';
});
export const getSelectedVulnCvss = createSelector([getSelectedVuln], (selectedVuln) => {
  return selectedVuln?.cvss ?? null;
});
export const getSelectedVulnCve = createSelector([getSelectedVuln], (selectedVuln) => {
  return selectedVuln?.cve ?? null;
});
export const getExploitability = createSelector([getSelectedVuln], (selectedVuln) => {
  return selectedVuln?.exploitability ?? null;
});
export const getSelectedVulnEpss = createSelector([getSelectedVuln], (selectedVuln) => {
  if (!selectedVuln?.epss || !selectedVuln?.epssPercentile) {
    return null;
  }

  return {
    score: selectedVuln.epss,
    percentile: selectedVuln.epssPercentile,
  };
});
export const getSelectedVulnDescription = createSelector([getSelectedVuln], (selectedVuln) => {
  return selectedVuln?.description ?? '';
});
//  Returns the data for the selected vulns details accordion section.
export const getSelectedVulnDetails = createSelector([getSelectedVuln], (selectedVuln) => {
  if (!selectedVuln) {
    return null;
  }

  return {
    cve: selectedVuln.cve,
    cwes: selectedVuln.cwes,
    affectedVersionRanges: selectedVuln.affectedVersionRanges,
    patchedVersionRanges: selectedVuln.patchedVersionRanges,
    published: selectedVuln.published,
    cveStatus: selectedVuln.cveStatus,
    epss: !(selectedVuln.epss && selectedVuln.epssPercentile) ? null : getHumanReadableEPSS({
      score: selectedVuln.epss,
      percentile: selectedVuln.epssPercentile,
    }).label,
  };
});

export const getSelectedVulnRemediation = createSelector([getSelectedVuln], (selectedVuln) => {
  return {
    completeFix: selectedVuln?.completeFix ?? UNABLE_TO_CALCULATE,
    completeFixDistance: selectedVuln?.completeFixDistance,
    partialFix: selectedVuln?.partialFix ?? UNABLE_TO_CALCULATE,
    partialFixDistance: selectedVuln?.partialFixDistance,
  };
});
export const getSelectedVulnNVDBaseMetrics = createSelector([getSelectedVuln], (selectedVuln) => {
  const metrics: CVSSMetric[] = [];

  // if the CVSSv3 vector is available, convert to human-readable KV pairs and use
  if (selectedVuln?.cvssV4Vector) {
    const cvssV4Metrics = getCVSSV4HumanReadableMetrics(selectedVuln.cvssV4Vector);
    // eslint-disable-next-line no-restricted-syntax
    for (const [shortname, value] of Object.entries(cvssV4Metrics ?? {})) {
      const details = CVSSv4BaseMetricNames[shortname];
      if (details && value) {
        metrics.push({ name: details.title, value });
      }
    }
  } else if (selectedVuln?.cvssV3Vector) {
    const cvssV3Metrics = getCVSSV3HumanReadableMetrics(selectedVuln.cvssV3Vector);
    // eslint-disable-next-line no-restricted-syntax
    for (const [shortname, value] of Object.entries(cvssV3Metrics ?? {})) {
      const details = CVSSv3BaseMetricNames[shortname];
      if (details && value) {
        metrics.push({ name: details.title, value });
      }
    }
  // else fallback to the CVSSv2 vector
  } else if (selectedVuln?.cvssV2Vector) {
    const cvssV2Metrics = getCVSSV2HumanReadableMetrics(selectedVuln.cvssV2Vector);
    // eslint-disable-next-line no-restricted-syntax
    for (const [shortname, value] of Object.entries(cvssV2Metrics ?? {})) {
      const details = CVSSv2BaseMetricNames[shortname];
      if (details && value) {
        metrics.push({ name: details.title, value });
      }
    }
  }

  return metrics;
});

export const getSelectedVulnReferences = createSelector([getSelectedVuln], (selectedVuln) => {
  return selectedVuln?.references ?? [];
});

export const getSelectedVersions = createPackageInventorySelector(
  (state: PackageInventoryStore) => state.selectedVersions);
export const getSelectedVersionsCount = createSelector(
  [getSelectedVersions],
  (selectedVersions) => selectedVersions.length,
);
export const getAreSomeVersionsSelected = createSelector(
  [getSelectedVersionsCount],
  (selectedVersionsCount) => selectedVersionsCount > 0,
);
export const getAreAllVersionsSelected = createSelector(
  [getSelectedVersionsCount, getPackageDetails],
  (selectedVersionsCount, details) => details?.length === selectedVersionsCount,
);
export const getDisplayedSelectedVersions = createSelector([getSelectedVersions], (selectedVersions) => {
  return selectedVersions.map(version => {
    const { revision } = Locator.parse(version.packageRevision);
    return {
      version: revision as string,
      packageRevision: version.packageRevision,
    };
  });
});

export const getIsVersionSelected = createSelector([
  getSelectedVersions,
  //  "Pass-through", meaning the component passes this through to the selector
  (_state: ReduxStore, packageRevision: string) => packageRevision,
], (selectedVersions, packageRevision) =>
  !!selectedVersions.find(version => version.packageRevision === packageRevision),
);
